// A small stroke icon set (24x24, currentColor), hand-drawn so the site needs
// no icon dependency. Decorative by default; pass `title` to make one speak.
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { title?: string; size?: number };

function make(paths: React.ReactNode) {
  return function Icon({ title, size = 20, ...rest }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden={title ? undefined : true}
        role={title ? 'img' : undefined}
        {...rest}
      >
        {title ? <title>{title}</title> : null}
        {paths}
      </svg>
    );
  };
}

export const IconMenu = make(<path d="M4 7h16M4 12h16M4 17h16" />);
export const IconClose = make(<path d="M6 6l12 12M18 6L6 18" />);
export const IconUser = make(
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
  </>,
);
export const IconLogout = make(
  <>
    <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
    <path d="M10 17l-5-5 5-5M5 12h11" />
  </>,
);
export const IconDownload = make(
  <>
    <path d="M12 4v11M7 10l5 5 5-5" />
    <path d="M5 20h14" />
  </>,
);
export const IconAndroid = make(
  <>
    <path d="M6 10h12v7a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2z" />
    <path d="M7 10a5 5 0 0 1 10 0M8 4l1.5 2.5M16 4l-1.5 2.5M4 11v5M20 11v5" />
    <circle cx="10" cy="7.5" r=".4" fill="currentColor" />
    <circle cx="14" cy="7.5" r=".4" fill="currentColor" />
  </>,
);
export const IconApple = make(
  <>
    <path d="M16.4 12.7c0-2.2 1.8-3.2 1.9-3.3-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.7.8-3.4.8-.7 0-1.8-.8-2.9-.8-1.5 0-2.9.9-3.7 2.2-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.7 2.3 2.8 2.2 1.1 0 1.6-.7 2.9-.7 1.4 0 1.7.7 2.9.7 1.2 0 2-1.1 2.7-2.2.9-1.3 1.2-2.5 1.2-2.6 0 0-2.3-.9-2.3-3.6z" />
    <path d="M14.2 5.4c.6-.8 1-1.8.9-2.8-.9 0-2 .6-2.6 1.4-.6.7-1.1 1.7-.9 2.7 1 .1 2-.5 2.6-1.3z" />
  </>,
);
export const IconDiscord = make(
  <>
    <path d="M8.5 7.5c-1.4.3-2.6.8-3.6 1.4C3.3 12 3 15 3.2 17.8c1.3 1 2.7 1.6 4.1 2l.9-1.6" />
    <path d="M15.5 7.5c1.4.3 2.6.8 3.6 1.4 1.6 3.1 1.9 6.1 1.7 8.9-1.3 1-2.7 1.6-4.1 2l-.9-1.6" />
    <path d="M7.5 16.5c3 1.5 6 1.5 9 0M9 6.5c2-.5 4-.5 6 0" />
    <circle cx="9.5" cy="12.5" r="1" />
    <circle cx="14.5" cy="12.5" r="1" />
  </>,
);
export const IconFacebook = make(<path d="M14 8h3V4h-3a4 4 0 0 0-4 4v3H7v4h3v6h4v-6h3l1-4h-4V8.5a.5.5 0 0 1 .5-.5z" />);

/** The platforms' own filled logos (Simple Icons paths), for the brand-coloured buttons. */
function brand(d: string) {
  const Icon = make(<path d={d} />);
  return (props: IconProps) => <Icon fill="currentColor" stroke="none" {...props} />;
}
export const IconDiscordMark = brand(
  'M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z',
);
export const IconFacebookMark = brand(
  'M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647z',
);
export const IconShield = make(<path d="M12 3l7 3v5c0 5-3 8.5-7 10-4-1.5-7-5-7-10V6z" />);
export const IconSword = make(
  <>
    <path d="M14.5 17.5L3 6V3h3l11.5 11.5" />
    <path d="M13 19l6-6M16 16l4 4M19 21l2-2" />
  </>,
);
export const IconCrown = make(<path d="M3 8l4.5 4L12 5l4.5 7L21 8l-2 11H5z" />);
export const IconCoins = make(
  <>
    <ellipse cx="9" cy="7" rx="6" ry="3" />
    <path d="M3 7v5c0 1.7 2.7 3 6 3s6-1.3 6-3V7" />
    <path d="M9 15v2c0 1.7 2.7 3 6 3s6-1.3 6-3v-5c0-1.6-2.4-2.9-5.5-3" />
  </>,
);
export const IconUsers = make(
  <>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20c1-3.5 3.5-5.5 6.5-5.5s5.5 2 6.5 5.5" />
    <path d="M16 4.5a3.5 3.5 0 0 1 0 7M18 14.8c1.8.7 3 2.4 3.6 5.2" />
  </>,
);
export const IconClock = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>,
);
export const IconChevronRight = make(<path d="M9 5l7 7-7 7" />);
export const IconChevronLeft = make(<path d="M15 5l-7 7 7 7" />);
export const IconChevronDown = make(<path d="M5 9l7 7 7-7" />);
export const IconCheck = make(<path d="M4 12.5l5 5L20 6.5" />);
export const IconCopy = make(
  <>
    <rect x="8" y="8" width="12" height="12" rx="1.5" />
    <path d="M16 8V5.5A1.5 1.5 0 0 0 14.5 4h-9A1.5 1.5 0 0 0 4 5.5v9A1.5 1.5 0 0 0 5.5 16H8" />
  </>,
);
export const IconAlert = make(
  <>
    <path d="M12 3l9.5 17h-19z" />
    <path d="M12 10v4M12 17.5v.01" />
  </>,
);
export const IconInfo = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v6M12 7.5v.01" />
  </>,
);
export const IconExternal = make(<path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />);
export const IconTrophy = make(
  <>
    <path d="M7 4h10v5a5 5 0 0 1-10 0z" />
    <path d="M7 6H4a3 3 0 0 0 3 4M17 6h3a3 3 0 0 1-3 4M12 14v4M8 21h8M9 18h6" />
  </>,
);
export const IconMap = make(
  <>
    <path d="M9 4l-6 2.5v14L9 18l6 2.5 6-2.5v-14L15 6.5z" />
    <path d="M9 4v14M15 6.5v14" />
  </>,
);
export const IconStore = make(
  <>
    <path d="M4 9l1.5-5h13L20 9" />
    <path d="M4 9a2.7 2.7 0 0 0 5.3 0 2.7 2.7 0 0 0 5.4 0A2.7 2.7 0 0 0 20 9M5 11v9h14v-9M10 20v-5h4v5" />
  </>,
);
export const IconSkull = make(
  <>
    <path d="M12 3a8 8 0 0 0-5 14.3V20h10v-2.7A8 8 0 0 0 12 3z" />
    <circle cx="9" cy="11" r="1.5" />
    <circle cx="15" cy="11" r="1.5" />
    <path d="M10 20v-2M14 20v-2" />
  </>,
);
export const IconBook = make(
  <>
    <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" />
    <path d="M4 21a2 2 0 0 1 2-2h13v2M9 7h6" />
  </>,
);
export const IconVote = make(
  <>
    <path d="M7 11l3-7a2 2 0 0 1 2 2v4h6a2 2 0 0 1 2 2.3l-1.3 7A2 2 0 0 1 16.7 21H7z" />
    <path d="M3 11h4v10H3z" />
  </>,
);
export const IconHandshake = make(
  <>
    <path d="M3 11l4-4 5 1 3-2 6 5" />
    <path d="M3 11l6 6a1.5 1.5 0 0 0 2-2l1 1a1.5 1.5 0 0 0 2-2l1 1a1.5 1.5 0 0 0 2-2l-5-5" />
    <path d="M17 13l-2-2" />
  </>,
);
export const IconWallet = make(
  <>
    <path d="M4 7a2 2 0 0 1 2-2h11v4" />
    <rect x="4" y="7" width="16" height="12" rx="2" />
    <path d="M16 13h.01" />
  </>,
);
export const IconLock = make(
  <>
    <rect x="5" y="10" width="14" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </>,
);
export const IconMail = make(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3.5 6.5l8.5 6.5 8.5-6.5" />
  </>,
);
export const IconKey = make(
  <>
    <circle cx="8" cy="15" r="4" />
    <path d="M11 12l9-9M17 6l3 3M15 8l2 2" />
  </>,
);
export const IconSettings = make(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2.8v2.4M12 18.8v2.4M4.2 7.5l2 1.2M17.8 15.3l2 1.2M4.2 16.5l2-1.2M17.8 8.7l2-1.2" />
    <circle cx="12" cy="12" r="7" />
  </>,
);
export const IconPlay = make(<path d="M8 5v14l11-7z" />);
export const IconSpark = make(<path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M6 18l2.5-2.5M15.5 8.5L18 6" />);
export const IconSearch = make(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-4-4" />
  </>,
);
export const IconRefresh = make(<path d="M20 11a8 8 0 1 0-2.3 5.7M20 5v6h-6" />);
export const IconCastle = make(
  <>
    <path d="M4 21V9h3V6h2v3h2V4h2v5h2V6h2v3h3v12z" />
    <path d="M10 21v-5h4v5" />
  </>,
);
export const IconGift = make(
  <>
    <rect x="3" y="8" width="18" height="5" rx="1" />
    <path d="M5 13v8h14v-8M12 8v13M12 8S10.5 3 8 4s0 4 4 4zM12 8s1.5-5 4-4 0 4-4 4z" />
  </>,
);
export const IconArrowRight = make(<path d="M4 12h16M14 6l6 6-6 6" />);
export const IconEye = make(
  <>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);
export const IconEyeOff = make(
  <>
    <path d="M3 3l18 18M10.6 5.1A10 10 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4.2M6.6 6.6C3.8 8.4 2 12 2 12s3.5 7 10 7a9.6 9.6 0 0 0 5.4-1.6" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </>,
);
